<template>
  <div class="analysis-configurator">
    <!-- Section expansível com transição -->
    <Transition name="slide-fade" appear>
      <div v-if="isOpen" class="mb-0 overflow-hidden bg-white shadow">
        <div class="flex items-center justify-between bg-gray-50 px-4 py-3">
          <div class="flex items-center space-x-3">
            <button @click="$emit('back')" class="p-1 text-gray-500 transition-colors hover:text-gray-700" title="Voltar ao seletor">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h3 class="text-lg font-semibold text-gray-800">Análise de Ciclos</h3>
          </div>
        </div>

        <!-- Conteúdo da seção -->
        <div class="px-6">
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <!-- Configurações -->
            <div class="overflow-y-auto">
              <CycleAnalysisControls
                :config="config"
                :is-analyzing="isAnalyzing"
                :sensor-data="sensorData"
                :sensor-index="sensorIndex"
                @update:config="updateConfig"
                @execute="executeWithFormData"
                @cancel="$emit('close')"
                @sensor-updated="handleSensorUpdated"
                ref="cycleAnalysisControlsRef" />
            </div>

            <!-- Resultados -->
            <div class="overflow-y-auto">
              <CycleAnalysisResults :result="storeAnalysisResult" @clear="clearStoreResults" @select-period="selectPeriod" />
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end bg-gray-2 p-4">
          <div class="flex">
            <button
              @click="$emit('back')"
              class="bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500">
              Voltar
            </button>

            <div class="ml-4 flex items-center space-x-4">
              <div class="flex items-center justify-between">
                <button
                  @click="saveParameters"
                  :disabled="isSaving"
                  class="bg-green-600 px-4 py-2 font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50">
                  <span v-if="isSaving" class="flex items-center">
                    <svg class="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvando...
                  </span>
                  <span v-else class="flex items-center"> Salvar Parâmetros </span>
                </button>
              </div>

              <div v-if="error" class="text-sm text-red-600">
                {{ error }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useCycleAnalysis } from '@/composables/useCycleAnalysis'
import { useRegisterStore } from '@/store/register'
import { computed, ref, watch } from 'vue'
import CycleAnalysisControls from './CycleAnalysis/CycleAnalysisControls.vue'
import CycleAnalysisResults from './CycleAnalysis/CycleAnalysisResults.vue'

// Props e Emits
interface Props {
  isOpen: boolean
  sensorData?: any
  startDate?: Date
  endDate?: Date
  sensorIndex?: number
}

interface Emits {
  (e: 'close'): void
  (e: 'update-periods', periods: any[]): void
  (e: 'back'): void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  sensorData: null,
  startDate: null,
  endDate: null,
  sensorIndex: null,
})

const emit = defineEmits<Emits>()

// Store
const registerStore = useRegisterStore()

// Referência para o componente CycleAnalysisControls
const cycleAnalysisControlsRef = ref()

// Usar o composable de análise de ciclo
const { isAnalyzing, analysisResult, error, config, periods, runAnalysis: runAnalysisBase, clearResults, updateConfig: updateConfigBase } = useCycleAnalysis()

// Estado para controle do botão salvar
const isSaving = ref(false)

// Computed para obter resultados da store
const storeAnalysisResult = computed(() => {
  if (props.sensorIndex === null) return null
  return registerStore.getAnalysisResults(props.sensorIndex)
})

// Wrapper para updateConfig com logs
const updateConfig = (newConfig: any) => {
  updateConfigBase(newConfig)
  if (props.sensorIndex !== null) {
    registerStore.updateAnalysisParameters(props.sensorIndex, newConfig)
  }
}

const executeWithFormData = async (formData: any) => {
  if (!props.sensorData || !props.startDate || !props.endDate || props.sensorIndex === null) return

  try {
    // Executar análise diretamente na store com os parâmetros do formulário
    await registerStore.executeAnalysis(props.sensorIndex, formData)
  } catch {
    // Erro ao executar análise
  }
}

// Função para salvar parâmetros na API
const saveParameters = async () => {
  if (props.sensorIndex === null || !props.sensorData) return

  try {
    isSaving.value = true

    // Atualizar parâmetros na store primeiro
    registerStore.updateAnalysisParameters(props.sensorIndex, config.value)

    // Usar a nova funcionalidade do CycleAnalysisControls para salvar e atualizar o sensor
    if (cycleAnalysisControlsRef.value) {
      await cycleAnalysisControlsRef.value.updateSensorInStore()
    } else {
      // Fallback para o método anterior
      await registerStore.saveMixingCycleSettings(props.sensorData.sensor.id, config.value)
    }
  } catch {
    // Erro ao salvar parâmetros
  } finally {
    isSaving.value = false
  }
}

// Função para selecionar período
const selectPeriod = (period: any) => {
  // Aqui você pode implementar lógica adicional para destacar o período no gráfico
}

// Função para limpar resultados
const clearStoreResults = () => {
  if (props.sensorIndex !== null) {
    registerStore.setAnalysisResults(props.sensorIndex, null)
    registerStore.clearAnalysisAnnotations(props.sensorIndex)

    // Emitir evento para atualizar o gráfico
    emit('update-periods', [])
  }
}

// Carregar parâmetros da store quando o componente abrir
watch(
  () => props.isOpen,
  async (newValue) => {
    if (newValue && props.sensorIndex !== null) {
      await registerStore.loadAnalysisParameters(props.sensorIndex)
      const parameters = registerStore.getAnalysisParameters(props.sensorIndex)
      updateConfigBase(parameters)

      clearResults()
    }
  },
)

// Watcher para monitorar mudanças nos resultados da store
watch(
  storeAnalysisResult,
  (newResult) => {
    if (newResult && newResult.periods) {
      emit('update-periods', newResult.periods)
    }
  },
  { deep: true, immediate: true },
)

// Função para lidar com atualizações no sensor
const handleSensorUpdated = () => {
  // Sensor atualizado com sucesso
  // ou atualizar outros componentes que dependem dos dados do sensor
}
</script>

<style scoped>
/* Transições suaves */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

/* Scrollbar personalizada */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
